// ============================================
// components/ProjectCard.tsx
//
// 项目缩略卡片. 首页 feed / 用户主页 / 收藏夹 / 搜索结果复用.
// Week 2 重做: 套官网设计系统 (.card-hover / brand / ink / .pill / .ex-tag),
//   跟 hipiido_web_v3.2.1 卡片视觉一致.
//
// 数据来源: community-list-projects 返回的卡片结构,
//   每项带 author { short_id, nickname, avatar_url, level }(后端 _attachAuthors 批量附加).
// ============================================

import { Link } from 'react-router-dom';
import { Puzzle, Heart, Bookmark, MessageCircle, User, type LucideIcon } from 'lucide-react';
import { categoryLabel } from '../services/categories';
import Avatar from './Avatar';
import type { ProjectPublic, ProjectCategory } from '../types/community';

// feed 接口返回的精简卡片结构(比 ProjectPublic 少正文/源文件)
export interface ProjectCardData extends Partial<ProjectPublic> {
  short_id: string;
  title: string;
  cover_url: string | null;
  category: ProjectCategory;
  tags: string[];
  status: 'pending_audit' | 'published' | 'rejected' | 'hidden' | 'deleted';
  board_type: string;
  likes_count: number;
  collections_count: number;
  comments_count: number;
  author_user_id?: string;
  author?: {
    short_id: string | null;
    nickname: string;
    avatar_url: string | null;
    level?: number;
  };
}

interface Props {
  project: ProjectCardData;
  /** 是否显示作者信息(首页 feed 需要,用户主页不需要) */
  showAuthor?: boolean;
}

export default function ProjectCard({ project, showAuthor }: Props) {
  const author = project.author;

  return (
    <Link
      to={`/p/${project.short_id}`}
      className="card-hover group block bg-white rounded-2xl border border-ink-300/40 overflow-hidden"
    >
      {/* 封面 */}
      <div className="aspect-video bg-gradient-to-br from-gold-50 to-gold-100 relative overflow-hidden">
        {project.cover_url ? (
          <img
            src={project.cover_url}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Puzzle className="w-12 h-12 text-gold-300" strokeWidth={1.5} />
          </div>
        )}

        {/* 分类胶囊(右上) */}
        <span className="pill absolute top-2.5 right-2.5 bg-white/90 text-gold-700 backdrop-blur-sm">
          {categoryLabel(project.category) || project.category}
        </span>

        {/* 审核中徽章(左上) */}
        {project.status === 'pending_audit' && (
          <span className="pill absolute top-2.5 left-2.5 bg-amber-100/95 text-amber-800 backdrop-blur-sm">
            审核中
          </span>
        )}
      </div>

      {/* 信息区 */}
      <div className="p-4">
        <h3 className="font-bold text-ink-900 leading-snug line-clamp-2 min-h-[2.6rem] group-hover:text-gold-700 transition-colors">
          {project.title}
        </h3>

        {/* 作者 */}
        {showAuthor && author && (
          <div className="flex items-center gap-2 mt-2.5">
            <Avatar name={author.nickname} size={20} className="!rounded-full" />
            <span className="text-xs text-ink-500 truncate">{author.nickname}</span>
          </div>
        )}

        {/* 标签 */}
        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {project.tags.slice(0, 3).map((t) => (
              <span key={t} className="ex-tag">
                {t}
              </span>
            ))}
          </div>
        )}

        {/* 底部:互动统计 + 板型 */}
        <div className="flex items-center gap-3 mt-3.5 pt-3 border-t border-ink-300/30 text-xs text-ink-400">
          <Stat icon={Heart} value={project.likes_count} />
          <Stat icon={Bookmark} value={project.collections_count} />
          <Stat icon={MessageCircle} value={project.comments_count} />
          <span className="ml-auto ex-tag !bg-ink-900/5 !text-ink-500">
            {project.board_type}
          </span>
        </div>
      </div>
    </Link>
  );
}

function Stat({ icon: Icon, value }: { icon: LucideIcon; value: number }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className="w-3.5 h-3.5 text-gold-400" strokeWidth={2} />
      <span className="tabular-nums">{value}</span>
    </span>
  );
}
